import React, { useState, useEffect } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronsUpDown, AlertTriangle, AlertCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAnomalias, CodigoAnomalia } from '@/hooks/useAnomalias';

interface AnomaliaSelectorProps {
  tipoSistema: string;
  onSelect: (codigo: CodigoAnomalia) => void;
  disabled?: boolean;
}

const getSeverityIcon = (severidad: string) => {
  switch (severidad) {
    case 'crítica':
      return <AlertTriangle className="h-3 w-3 text-red-500" />;
    case 'alta':
      return <AlertCircle className="h-3 w-3 text-orange-500" />;
    case 'media':
      return <Clock className="h-3 w-3 text-yellow-500" />;
    case 'baja':
      return <Clock className="h-3 w-3 text-blue-500" />;
    default:
      return <Clock className="h-3 w-3 text-gray-500" />;
  }
};

const getSeverityColor = (severidad: string) => {
  switch (severidad) {
    case 'crítica':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'alta':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'media':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'baja':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const AnomaliaSelector = ({ tipoSistema, onSelect, disabled }: AnomaliaSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { codigosAnomalias, cargarCodigosAnomalias } = useAnomalias();

  useEffect(() => {
    if (tipoSistema) {
      cargarCodigosAnomalias(tipoSistema);
    } else {
      cargarCodigosAnomalias();
    }
  }, [tipoSistema]);

  const normalize = (s: string) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  const q = normalize(search);
  const filteredCodigos = !q
    ? codigosAnomalias
    : codigosAnomalias.filter((c) => {
        const haystack = normalize(
          [c.codigo, c.descripcion, c.solucion_sugerida, (c as any).referencia_normativa, (c as any).justificacion_normativa]
            .filter(Boolean)
            .join(' ')
        );
        return q.split(/\s+/).every((term) => haystack.includes(term));
      });

  const handleSelect = (codigo: CodigoAnomalia) => {
    onSelect(codigo);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          <span className="text-muted-foreground">
            Seleccionar código de anomalía...
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(95vw,400px)] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Buscar código o descripción..." 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[300px] overflow-y-auto overscroll-contain">
            <CommandEmpty>No se encontraron códigos de anomalía.</CommandEmpty>
            <CommandGroup heading={`Anomalías para sistema ${tipoSistema || 'todos'}`}>
              {filteredCodigos.map((codigo) => (
                <CommandItem
                  key={codigo.codigo}
                  value={codigo.codigo}
                  onSelect={() => handleSelect(codigo)}
                  className="flex items-center justify-between py-3"
                >
                  <div className="flex items-center space-x-2 flex-1">
                    {getSeverityIcon(codigo.severidad)}
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono font-medium text-sm">
                          {codigo.codigo}
                        </span>
                        <Badge 
                          variant="outline" 
                          className={cn("text-xs", getSeverityColor(codigo.severidad))}
                        >
                          {codigo.severidad}
                        </Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {codigo.descripcion}
                      </span>
                      {codigo.solucion_sugerida && (
                        <span className="text-xs text-muted-foreground italic">
                          → {codigo.solucion_sugerida}
                        </span>
                      )}
                    </div>
                  </div>
                  <Check className="ml-2 h-4 w-4 opacity-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};